import { useGetResolutionReasonsQuery } from '@/store/api/services/operations/resolutionReasons';
import { useResolveServiceTicketTaskMutation } from '@/store/api/services/operations/serviceTickets/serviceTickets';
import type { ServiceTicketTask } from '@/types/serviceTicket';
import { useModal } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

interface ResolveServiceTicketTaskProps extends ModalChildrenProps {
  props: {
    serviceTicketId: string;
    task: ServiceTicketTask;
  } | null;
}

const ResolveServiceTicketTaskSchema = z.object({
  resolution: z.enum(['pending', 'success', 'failure']),
  resolution_reason_id: z.string().optional(),
  resolution_notes: z.string().optional(),
});

type ResolveServiceTicketTaskSchemaType = z.infer<typeof ResolveServiceTicketTaskSchema>;

/** Resolves a single service ticket task (status, reason, notes). */
const ResolveServiceTicketTask = ({ hide, props }: ResolveServiceTicketTaskProps) => {
  const serviceTicketId = props?.serviceTicketId;
  const task = props?.task;

  const { show } = useModal();

  const { data: resolutionReasonsResponse } = useGetResolutionReasonsQuery({
    pagination: {
      current_page: 1,
      items_per_page: 1000,
      sort_by: 'name',
      sort_ascending: true,
    },
  });
  const resolutionReasons = resolutionReasonsResponse?.data || [];

  const [resolveTask] = useResolveServiceTicketTaskMutation();

  const currentResolution = (task?.resolution?.toLowerCase() || 'pending') as
    | 'pending'
    | 'success'
    | 'failure';

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ResolveServiceTicketTaskSchemaType>({
    resolver: zodResolver(ResolveServiceTicketTaskSchema),
    mode: 'onSubmit',
    defaultValues: {
      // Default to "success" when the task is still pending, since resolving usually
      // means marking it done; otherwise keep the task's current resolution.
      resolution: currentResolution === 'pending' ? 'success' : currentResolution,
      resolution_reason_id: task?.resolution_reason_id || '',
      resolution_notes: task?.resolution_notes || '',
    },
  });

  const onSubmit = async (data: ResolveServiceTicketTaskSchemaType) => {
    if (!serviceTicketId || !task) {
      toast.error('Task is missing');
      return;
    }

    try {
      await resolveTask({
        serviceTicketId,
        taskId: task.id,
        resolution: data.resolution,
        resolution_reason_id: data.resolution_reason_id || undefined,
        resolution_notes: data.resolution_notes || undefined,
      }).unwrap();
      toast.success('Task resolution updated successfully');
      hide();
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message || error?.data?.message || error?.message || 'Failed to resolve task';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          {task && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {task.task_type_name}
            </Typography>
          )}

          <Controller
            name="resolution"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required>
                <InputLabel>Resolution Status</InputLabel>
                <Select {...field} label="Resolution Status">
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="failure">Failure</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          <Stack gap={0.5}>
            <Controller
              name="resolution_reason_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>Resolution Reason</InputLabel>
                  <Select {...field} label="Resolution Reason">
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {resolutionReasons.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.name}
                        {reason.is_success_reason && (
                          <Chip label="Success" color="success" size="small" sx={{ ml: 1 }} />
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => show('create-resolution-reason')}
            >
              + Create new resolution reason
            </Typography>
          </Stack>

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Resolution Notes
            </Typography>
            <Controller
              name="resolution_notes"
              control={control}
              render={({ field }) => (
                <TextareaAutosize
                  {...field}
                  minRows={3}
                  maxRows={6}
                  placeholder="Add any notes about how this task was resolved..."
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    padding: '12px 14px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    lineHeight: '1.5',
                    backgroundColor: 'transparent',
                    outline: 'none',
                    cursor: 'text',
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide()} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Resolution'}
        </Button>
      </DialogActions>
    </>
  );
};

export default ResolveServiceTicketTask;
